import React, { useState, useEffect } from 'react';
import {
  Sparkles,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Edit2,
  Save,
  RefreshCw,
  FileText,
  Zap,
  Globe,
  Database,
  DollarSign,
  Home,
  Calendar,
  Check,
  BookOpen
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { triggerAiExtraction, getAiExtractionTemplates, getRevenueTypes } from '../../utils/api';

/**
 * AI Extraction Tab - AI-powered data extraction
 *
 * This component sends PDF files directly to AI vision APIs (Claude or OpenAI) for analysis,
 * extracting data in database-ready format (not field mappings).
 *
 * Users select what data types to extract (revenue, roomStats, occupancy)
 * and the AI returns actual data values ready for database insertion.
 */

/**
 * Convert File to base64 string
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Remove data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

/**
 * Read File as text string (for CSV files)
 */
const fileToText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsText(file);
  });
};

/**
 * Convert an Excel workbook to CSV text for text-mode extraction.
 * Honors the schema builder's sheet selection ({mode, sheets}, stored in
 * schemaConfig.selectedPages for Excel uploads).
 */
const excelFileToCsvText = async (file, sheetSelection = null) => {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });

  let sheetNames = workbook.SheetNames;
  if (sheetSelection?.mode === 'single' && Array.isArray(sheetSelection.sheets)) {
    const wanted = sheetSelection.sheets.filter(name => workbook.SheetNames.includes(name));
    if (wanted.length > 0) sheetNames = wanted;
  }

  const parts = sheetNames
    .map(name => {
      const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[name], { blankrows: false });
      if (!csv.trim()) return null;
      return sheetNames.length > 1 ? `=== Sheet: ${name} ===\n${csv}` : csv;
    })
    .filter(Boolean);

  if (parts.length === 0) {
    throw new Error('The Excel file contains no data to extract');
  }
  return parts.join('\n\n');
};

const ConfidenceBadge = ({ value }) => {
  const percentage = Math.round(value * 100);
  const getColor = () => {
    if (percentage >= 80) return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/30';
    if (percentage >= 50) return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/30';
    return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30';
  };

  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getColor()}`}>
      {percentage}% confident
    </span>
  );
};

const DataTypeCheckbox = ({ id, label, description, icon: Icon, checked, onChange }) => (
  <label
    className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-colors ${
      checked
        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
    }`}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(id, e.target.checked)}
      className="sr-only"
    />
    <div className={`flex-shrink-0 w-5 h-5 rounded border-2 mr-3 mt-0.5 flex items-center justify-center ${
      checked
        ? 'bg-blue-500 border-blue-500'
        : 'border-gray-300 dark:border-gray-500'
    }`}>
      {checked && <Check className="w-3 h-3 text-white" />}
    </div>
    <div className="flex-1">
      <div className="flex items-center">
        <Icon className={`mr-2 ${checked ? 'text-blue-500' : 'text-gray-400'}`} size={18} />
        <span className="font-medium text-gray-900 dark:text-white">{label}</span>
      </div>
      <p className="cp-help">{description}</p>
    </div>
  </label>
);

const RevenueDataTable = ({ data, onTypeNameChange, editedNames = {} }) => {
  const [editingIdx, setEditingIdx] = useState(null);
  const [editValue, setEditValue] = useState('');

  if (!data || data.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">No revenue data extracted</p>;
  }

  const handleStartEdit = (idx, currentName) => {
    setEditingIdx(idx);
    setEditValue(editedNames[currentName] || currentName);
  };

  const handleSaveEdit = (originalName) => {
    if (editValue.trim() && editValue.trim() !== originalName) {
      onTypeNameChange?.(originalName, editValue.trim());
    }
    setEditingIdx(null);
    setEditValue('');
  };

  const handleKeyDown = (e, originalName) => {
    if (e.key === 'Enter') {
      handleSaveEdit(originalName);
    } else if (e.key === 'Escape') {
      setEditingIdx(null);
      setEditValue('');
    }
  };

  const getDisplayName = (row) => {
    return editedNames[row.revenueTypeName] || row.revenueTypeName;
  };

  const isEdited = (row) => {
    return editedNames[row.revenueTypeName] && editedNames[row.revenueTypeName] !== row.revenueTypeName;
  };

  return (
    <div className="overflow-auto max-h-[70vh]">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              Category <span className="text-purple-500 font-normal">(click to edit)</span>
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Today</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">MTD</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">YTD</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Budget</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {data.map((row, idx) => (
            <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
              <td className="px-3 py-2">
                {editingIdx === idx ? (
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={() => handleSaveEdit(row.revenueTypeName)}
                    onKeyDown={(e) => handleKeyDown(e, row.revenueTypeName)}
                    autoFocus
                    className="w-full px-2 py-1 text-sm border border-purple-400 rounded
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                ) : (
                  <div
                    onClick={() => handleStartEdit(idx, row.revenueTypeName)}
                    className="cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/20 px-2 py-1 rounded -mx-2"
                  >
                    <span className={`font-medium ${isEdited(row) ? 'text-purple-600 dark:text-purple-400' : 'text-gray-900 dark:text-white'}`}>
                      {getDisplayName(row)}
                    </span>
                    {isEdited(row) && (
                      <span className="ml-2 text-xs text-gray-400 line-through">
                        {row.revenueTypeName}
                      </span>
                    )}
                  </div>
                )}
              </td>
              <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                {row.currentValue != null ? `$${row.currentValue.toLocaleString()}` : '-'}
              </td>
              <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                {row.mtd != null ? `$${row.mtd.toLocaleString()}` : '-'}
              </td>
              <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                {row.ytd != null ? `$${row.ytd.toLocaleString()}` : '-'}
              </td>
              <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                {row.budgetAmount != null ? `$${row.budgetAmount.toLocaleString()}` : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RoomStatsCard = ({ data }) => {
  if (!data) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">No room stats extracted</p>;
  }

  const stats = [
    { label: 'Rooms Available', value: data.roomsAvailable },
    { label: 'Rooms Occupied', value: data.roomsOccupied },
    { label: 'Occupancy %', value: data.occupancyPercent != null ? `${data.occupancyPercent}%` : null },
    { label: 'ADR', value: data.adr != null ? `$${data.adr.toFixed(2)}` : null },
    { label: 'RevPAR', value: data.revPAR != null ? `$${data.revPAR.toFixed(2)}` : null },
    { label: 'Room Revenue', value: data.roomRevenue != null ? `$${data.roomRevenue.toLocaleString()}` : null },
    { label: 'Arrivals', value: data.arrivals },
    { label: 'Departures', value: data.departures },
    { label: 'Stayovers', value: data.stayovers },
    { label: 'No Shows', value: data.noShows },
    { label: 'Total Guests', value: data.totalGuests },
    { label: 'Comp Rooms', value: data.compRooms },
  ].filter(s => s.value != null);

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      {stats.map((stat, idx) => (
        <div key={idx} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <div className="cp-help">{stat.label}</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white">{stat.value}</div>
        </div>
      ))}
    </div>
  );
};

const OccupancyCard = ({ data }) => {
  if (!data || (Array.isArray(data) && data.length === 0)) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">No occupancy data extracted</p>;
  }

  // Per-stay-date rows (hotelOccupancy shape). Legacy single-object results
  // render as a one-row table.
  const rows = Array.isArray(data) ? data : [data];
  const columns = [
    { key: 'occupancyDate', label: 'Stay Date' },
    { key: 'totalRooms', label: 'Total Rooms' },
    { key: 'availableRooms', label: 'Available' },
    { key: 'otbGrpRooms', label: 'OTB Grp' },
    { key: 'otbTrnRooms', label: 'OTB Trn' },
    { key: 'prjGroupRooms', label: 'Prj Grp' },
    { key: 'prjTrnRooms', label: 'Prj Trn' },
    { key: 'otbGrpRevenue', label: 'OTB Grp $', currency: true },
    { key: 'otbTrnRevenue', label: 'OTB Trn $', currency: true },
    { key: 'totalRevenue', label: 'Total $', currency: true },
  ].filter(col => rows.some(r => r && r[col.key] != null));

  const fmt = (col, v) => {
    if (v == null) return '—';
    if (col.currency) return `$${Number(v).toLocaleString()}`;
    return String(v);
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col.key} className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className={idx % 2 === 1 ? 'bg-gray-50 dark:bg-gray-800' : ''}>
              {columns.map(col => (
                <td key={col.key} className="px-3 py-2 text-gray-900 dark:text-white whitespace-nowrap">{fmt(col, row[col.key])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AiExtractionTab = ({
  uploadedFile,
  extractedData,
  schemaConfig,
  onMappingChange,
  onExtractedData,
  onNext,
  onBack,
  onSkip,
  skipLabel = 'Skip AI Extraction',
  isSkipping = false,
  hideFooter = false,
  onFooterChange,
  selectedLocation
}) => {
  const [extractionStatus, setExtractionStatus] = useState('idle'); // idle, processing, completed, failed


  const [aiResult, setAiResult] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [modelChoice, setModelChoice] = useState('auto');
  // OpenRouter "bring your own model": modelChoice 'openrouter:<slug>'
  const [openRouterModels, setOpenRouterModels] = useState([]);        // PDF-capable — datalist suggestions
  const [openRouterAllModels, setOpenRouterAllModels] = useState([]);  // full catalog — typo validation
  const isOpenRouterChoice = typeof modelChoice === 'string' && modelChoice.startsWith('openrouter:');
  const openRouterSlug = isOpenRouterChoice ? modelChoice.slice('openrouter:'.length) : '';
  const [extractionId, setExtractionId] = useState(null);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState(null);

  // User prompt for custom extraction instructions
  const [userPrompt, setUserPrompt] = useState('');

  // Existing revenue types for mapping
  const [existingRevenueTypes, setExistingRevenueTypes] = useState([]);

  // Track user edits to revenue type names (original → corrected)
  // Format: { "Original Name": "Corrected Name", ... }
  const [revenueTypeOverrides, setRevenueTypeOverrides] = useState({});

  // Handler for when user edits a revenue type name
  const handleTypeNameChange = (originalName, newName) => {
    setRevenueTypeOverrides(prev => {
      const updated = { ...prev };
      if (newName === originalName) {
        // If reverted to original, remove the override
        delete updated[originalName];
      } else {
        updated[originalName] = newName;
      }
      return updated;
    });
  };

  // Data type selections (checkboxes)
  const [dataTypes, setDataTypes] = useState({
    revenue: true,
    roomStats: true,
    occupancy: false,
    journal: false
  });

  // Available data type configurations
  const dataTypeOptions = [
    {
      id: 'revenue',
      label: 'Revenue Data',
      description: 'Daily revenue by category (Room, F&B, Spa, etc.)',
      icon: DollarSign
    },
    {
      id: 'roomStats',
      label: 'Room Statistics',
      description: 'Occupancy, ADR, RevPAR, arrivals, departures',
      icon: Home
    },
    {
      id: 'occupancy',
      label: 'Occupancy Forecast',
      description: 'Projected and on-the-books room data',
      icon: Calendar
    },
    {
      id: 'journal',
      label: 'Journal Data',
      description: 'Daily revenue, tax, and payment totals from journal/flash reports',
      icon: BookOpen
    }
  ];

  // Load saved settings from schemaConfig (etlTableConfig)
  useEffect(() => {
    // Load saved user prompt if exists
    const savedPrompt = schemaConfig?.etlTableConfig?.userPrompt ||
                        schemaConfig?.aiExtraction?.userPrompt;
    if (savedPrompt) {
      setUserPrompt(savedPrompt);
    }

    // Load saved overrides if exists (convert array format to object format)
    const savedOverrides = schemaConfig?.etlTableConfig?.revenueTypeOverrides ||
                           schemaConfig?.aiExtraction?.revenueTypeOverrides;
    if (savedOverrides && Array.isArray(savedOverrides)) {
      const overridesObj = {};
      savedOverrides.forEach(o => {
        overridesObj[o.original] = o.corrected;
      });
      setRevenueTypeOverrides(overridesObj);
      console.log('📋 Loaded revenue type overrides:', savedOverrides.length, 'corrections');
    }

    // Load saved model preference
    const savedModel = schemaConfig?.etlTableConfig?.preferredModel ||
                       schemaConfig?.aiExtraction?.preferredModel;
    if (savedModel) {
      setModelChoice(savedModel);
    }
  }, [schemaConfig]);

  // Populate the OpenRouter model picker (PDF-capable models only) the first
  // time the OpenRouter tile is selected. Free-text write-in always works —
  // the catalog is a convenience, not a validation gate.
  useEffect(() => {
    if (!isOpenRouterChoice || openRouterModels.length > 0) return;
    fetch('https://openrouter.ai/api/v1/models')
      .then((r) => r.json())
      .then((data) => {
        const all = (data?.data || []).map((m) => m.id).sort();
        const pdfCapable = (data?.data || [])
          .filter((m) => (m.architecture?.input_modalities || []).includes('file'))
          .map((m) => m.id)
          .sort();
        if (all.length > 0) setOpenRouterAllModels(all);
        if (pdfCapable.length > 0) setOpenRouterModels(pdfCapable);
      })
      .catch((err) => console.warn('OpenRouter catalog fetch failed:', err));
  }, [isOpenRouterChoice, openRouterModels.length]);

  // Load templates and revenue types on mount
  useEffect(() => {
    const loadTemplates = async () => {
      if (selectedLocation?.orgId) {
        try {
          const response = await getAiExtractionTemplates(
            selectedLocation.orgId,
            selectedLocation.id
          );
          if (response.success && response.templates) {
            setTemplates(response.templates);
          }
        } catch (err) {
          console.error('Failed to load templates:', err);
        }
      }
    };

    const loadRevenueTypes = async () => {
      if (selectedLocation?.orgId && selectedLocation?.id) {
        try {
          const response = await getRevenueTypes(
            selectedLocation.orgId,
            selectedLocation.id
          );
          if (response.success && response.revenueTypes) {
            // Extract id, name, and mappedType for the prompt
            const types = response.revenueTypes.map(rt => ({
              id: rt.id,
              name: rt.name,
              mappedType: rt.mappedType || null
            }));
            setExistingRevenueTypes(types);
            console.log('📋 Loaded revenue types for AI extraction:', types.length);
            console.log('📋 Revenue types:', types.map(t => t.mappedType ? `${t.name} (${t.mappedType})` : t.name).join(', '));
          } else {
            console.log('📋 No existing revenue types found for this location');
          }
        } catch (err) {
          console.error('Failed to load revenue types:', err);
        }
      }
    };

    loadTemplates();
    loadRevenueTypes();
  }, [selectedLocation]);

  const handleDataTypeChange = (typeId, checked) => {
    setDataTypes(prev => ({ ...prev, [typeId]: checked }));
  };

  const getSelectedDataTypes = () => {
    return Object.entries(dataTypes)
      .filter(([_, checked]) => checked)
      .map(([type]) => type);
  };

  const handleExtract = async () => {
    const selectedTypes = getSelectedDataTypes();
    if (selectedTypes.length === 0) {
      setError('Please select at least one data type to extract');
      return;
    }

    setExtractionStatus('processing');
    setError(null);

    try {
      // Determine file type
      const fileNameLower = uploadedFile?.name?.toLowerCase() || '';
      const isPdfFile = uploadedFile &&
        (uploadedFile.type === 'application/pdf' || fileNameLower.endsWith('.pdf'));
      const isCsvFile = uploadedFile &&
        (uploadedFile.type === 'text/csv' || fileNameLower.endsWith('.csv'));
      const isXmlFile = uploadedFile &&
        (uploadedFile.type === 'text/xml' || uploadedFile.type === 'application/xml' || fileNameLower.endsWith('.xml'));
      const isTxtFile = uploadedFile &&
        (uploadedFile.type === 'text/plain' || fileNameLower.endsWith('.txt'));
      const isExcelFile = uploadedFile &&
        (fileNameLower.endsWith('.xlsx') || fileNameLower.endsWith('.xls') || fileNameLower.endsWith('.xlsm'));

      if (!isPdfFile && !isCsvFile && !isXmlFile && !isTxtFile && !isExcelFile) {
        throw new Error('Please upload a PDF, CSV, XML, TXT, or Excel file for AI extraction');
      }

      const isTextMode = isCsvFile || isXmlFile || isTxtFile || isExcelFile;
      console.log(`Using ${isTextMode ? 'Text' : 'PDF'} Data Extraction mode (${isExcelFile ? 'Excel' : isXmlFile ? 'XML' : isTxtFile ? 'TXT' : isCsvFile ? 'CSV' : 'PDF'})`);
      console.log('Selected data types:', selectedTypes);

      // Get saved overrides from config (may have been loaded from previous extractions)
      const savedOverrides = schemaConfig?.etlTableConfig?.revenueTypeOverrides ||
                             schemaConfig?.aiExtraction?.revenueTypeOverrides || null;

      const extractionParams = {
        orgId: selectedLocation.orgId,
        locationId: selectedLocation.id,
        dataTypes: selectedTypes,
        sourceFileName: uploadedFile?.name || schemaConfig.fileName || 'uploaded_file',
        sourceFileType: isExcelFile ? 'xlsx' : isXmlFile ? 'xml' : isTxtFile ? 'txt' : isCsvFile ? 'csv' : 'pdf',
        templateId: selectedTemplate?.id,
        preferredModel: modelChoice,
        // For Excel, selectedPages holds a sheet selection that's already
        // applied client-side during CSV conversion — only PDFs need it server-side.
        selectedPages: isPdfFile ? schemaConfig.selectedPages : null,
        userPrompt: userPrompt.trim() || null,
        existingRevenueTypes: existingRevenueTypes.length > 0 ? existingRevenueTypes : null,
        revenueTypeOverrides: savedOverrides
      };

      if (isExcelFile) {
        // Convert Excel workbook to CSV text (honoring sheet selection)
        extractionParams.documentContent = await excelFileToCsvText(uploadedFile, schemaConfig.selectedPages);
      } else if (isTextMode) {
        // Read CSV/XML as text content
        const textContent = await fileToText(uploadedFile);
        extractionParams.documentContent = textContent;
      } else {
        // Read PDF as base64
        const pdfBase64 = await fileToBase64(uploadedFile);
        extractionParams.pdfBase64 = pdfBase64;
      }

      console.log('🚀 AI Extraction params:', {
        ...extractionParams,
        pdfBase64: extractionParams.pdfBase64 ? `[${extractionParams.pdfBase64.length} chars]` : undefined,
        documentContent: extractionParams.documentContent ? `[${extractionParams.documentContent.length} chars]` : undefined,
        existingRevenueTypes: existingRevenueTypes.length > 0
          ? `${existingRevenueTypes.length} types: ${existingRevenueTypes.map(t => t.name).join(', ')}`
          : 'none'
      });

      const response = await triggerAiExtraction(extractionParams);

      if (response.success) {
        setExtractionId(response.extractionId);
        setAiResult({
          businessDate: response.businessDate,
          extractedData: response.extractedData,
          documentInfo: response.documentInfo,
          confidence: response.confidence
        });
        setMetrics(response.metrics);
        setExtractionStatus('completed');

        // Notify parent of extracted data
        if (onExtractedData) {
          onExtractedData({
            extractionId: response.extractionId,
            businessDate: response.businessDate,
            extractedData: response.extractedData,
            documentInfo: response.documentInfo,
            dataTypes: selectedTypes,
            preferredModel: modelChoice,
            userPrompt: userPrompt.trim() || null
          });
        }
      } else {
        throw new Error(response.error || 'Extraction failed');
      }
    } catch (err) {
      console.error('AI Extraction error:', err);
      setError(err.message);
      setExtractionStatus('failed');
    }
  };

  const handleSaveData = () => {
    // Pass extracted data to parent for saving/import
    if (onExtractedData && aiResult) {
      // Build overrides in before→after format for storage
      const overridesForStorage = Object.keys(revenueTypeOverrides).length > 0
        ? Object.entries(revenueTypeOverrides).map(([original, corrected]) => ({
            original,
            corrected
          }))
        : null;

      onExtractedData({
        extractionId,
        businessDate: aiResult.businessDate,
        extractedData: aiResult.extractedData,
        documentInfo: aiResult.documentInfo,
        dataTypes: getSelectedDataTypes(),
        preferredModel: modelChoice,
        userPrompt: userPrompt.trim() || null,
        revenueTypeOverrides: overridesForStorage,
        confirmed: true
      });
    }
    onNext();
  };

  /**
   * Publish this step's navigation to the wizard frame.
   *
   * Extraction has no "Continue" of its own — confirming the result inside the
   * step advances the flow — so the frame gets a Skip secondary and a note
   * explaining what happens next, rather than a dead primary button.
   */
  useEffect(() => {
    if (!onFooterChange) return;
    onFooterChange({
      secondary: onSkip ? (
        <button type="button" className="cp-btn cp-ghost" onClick={onSkip} disabled={isSkipping || extractionStatus === 'processing'}>{isSkipping ? 'Reading file…' : skipLabel}</button>
      ) : null,
      note: 'Confirm the extracted data below to continue.',
    });
  }, [onFooterChange, onSkip, skipLabel, isSkipping, extractionStatus]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
          <Sparkles className="mr-2 text-purple-500" size={20} />
          AI-Powered Data Extraction
        </h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          AI will analyze your document and extract data in database-ready format.
          Select the types of data you expect in this document.
        </p>
      </div>

      {/* Configuration Section */}
      {extractionStatus === 'idle' && (
        <div className="space-y-6">
          {/* Data Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              What data should be extracted?
            </label>
            <div className="grid gap-3">
              {dataTypeOptions.map(option => (
                <DataTypeCheckbox
                  key={option.id}
                  id={option.id}
                  label={option.label}
                  description={option.description}
                  icon={option.icon}
                  checked={dataTypes[option.id]}
                  onChange={handleDataTypeChange}
                />
              ))}
            </div>
          </div>

          {/* Template Selection */}
          {templates.length > 0 && (
            <div>
              <label className="cp-label">
                Use Existing Template (Optional)
              </label>
              <select
                value={selectedTemplate?.id || ''}
                onChange={(e) => {
                  const template = templates.find(t => t.id === e.target.value);
                  setSelectedTemplate(template || null);
                }}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Start fresh (no template)</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.usageCount} uses)
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Model Selection */}
          <div>
            <label className="cp-label">
              AI Model
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => setModelChoice('auto')}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  modelChoice === 'auto'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-center mb-1">
                  <Zap className="text-blue-500" size={18} />
                </div>
                <div className="cp-name">Auto</div>
                <div className="cp-help">Cost optimized</div>
              </button>
              <button
                onClick={() => setModelChoice('haiku')}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  modelChoice === 'haiku'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-center mb-1">
                  <FileText className="text-green-500" size={18} />
                </div>
                <div className="cp-name">Haiku</div>
                <div className="cp-help">~$0.10/doc</div>
              </button>
              <button
                onClick={() => setModelChoice('sonnet')}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  modelChoice === 'sonnet'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-center mb-1">
                  <Sparkles className="text-purple-500" size={18} />
                </div>
                <div className="cp-name">Sonnet</div>
                <div className="cp-help">~$0.50/doc</div>
              </button>
              <button
                onClick={() => setModelChoice('gpt-4.1')}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  modelChoice === 'gpt-4.1'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-center mb-1">
                  <svg className="text-emerald-500" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
                  </svg>
                </div>
                <div className="cp-name">GPT-4.1</div>
                <div className="cp-help">~$0.20/doc</div>
              </button>
              <button
                onClick={() => setModelChoice('gpt-5.2')}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  modelChoice === 'gpt-5.2'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-center mb-1">
                  <svg className="text-teal-500" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
                  </svg>
                </div>
                <div className="cp-name">GPT-5.2</div>
                <div className="cp-help">~$1.00/doc</div>
              </button>
              <button
                onClick={() => setModelChoice('gpt-5.6-luna')}
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  modelChoice === 'gpt-5.6-luna'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-center mb-1">
                  <svg className="text-cyan-500" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
                  </svg>
                </div>
                <div className="cp-name">GPT-5.6 Luna</div>
                <div className="cp-help">~$0.15/doc</div>
              </button>
              <button
                onClick={() =>
                  setModelChoice((prev) =>
                    typeof prev === 'string' && prev.startsWith('openrouter:')
                      ? prev
                      : 'openrouter:anthropic/claude-haiku-4.5'
                  )
                }
                className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                  isOpenRouterChoice
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-center mb-1">
                  <Globe className="text-indigo-500" size={18} />
                </div>
                <div className="cp-name">OpenRouter</div>
                <div className="cp-help">Any model</div>
              </button>
            </div>
            {isOpenRouterChoice && (
              <div className="mt-3">
                <label className="cp-label">
                  OpenRouter model
                </label>
                <input
                  list="openrouter-model-catalog"
                  value={openRouterSlug}
                  onChange={(e) => setModelChoice(`openrouter:${e.target.value.trim()}`)}
                  placeholder="anthropic/claude-haiku-4.5"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           placeholder-gray-400 dark:placeholder-gray-500
                           focus:ring-2 focus:ring-purple-500 focus:border-purple-500
                           text-sm font-mono"
                />
                <datalist id="openrouter-model-catalog">
                  {openRouterModels.map((id) => (
                    <option key={id} value={id} />
                  ))}
                </datalist>
                {openRouterAllModels.length > 0 && openRouterSlug && !openRouterAllModels.includes(openRouterSlug) ? (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    "{openRouterSlug}" is not in the OpenRouter catalog — check the slug (e.g. z-ai/glm-5.2, not z.ai). Unknown slugs fail at extraction time.
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Pick from the catalog (PDF-capable models) or type any OpenRouter model slug.
                    Usage is billed to the OpenRouter account.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Custom Instructions */}
          <div>
            <label className="cp-label">
              Custom Instructions (Optional)
            </label>
            <textarea
              value={userPrompt}
              onChange={(e) => setUserPrompt(e.target.value)}
              placeholder="Add specific instructions for this document type, e.g.:
• Extract only from the Restaurant Summary table
• Use NET column values instead of GROSS
• Ignore the statistics section
• Room revenue is labeled as 'Lodging' in this report"
              rows={4}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       placeholder-gray-400 dark:placeholder-gray-500
                       focus:ring-2 focus:ring-purple-500 focus:border-purple-500
                       text-sm resize-none"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              These instructions will be saved and used each time this document type is processed automatically.
            </p>
          </div>

          {/* AI Mode Indicator */}
          {uploadedFile && (uploadedFile.type === 'application/pdf' || uploadedFile.name?.toLowerCase().endsWith('.pdf')) && (
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
              <div className="flex items-center text-sm text-purple-700 dark:text-purple-300">
                <Sparkles className="mr-2 text-purple-500" size={16} />
                <span className="font-medium">Vision Mode Active</span>
              </div>
              <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                PDF will be sent directly to AI for visual analysis - no OCR needed.
              </p>
            </div>
          )}
          {uploadedFile && (uploadedFile.type === 'text/csv' || uploadedFile.name?.toLowerCase().endsWith('.csv')) && (
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
              <div className="flex items-center text-sm text-purple-700 dark:text-purple-300">
                <Sparkles className="mr-2 text-purple-500" size={16} />
                <span className="font-medium">CSV Text Mode Active</span>
              </div>
              <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                CSV content will be sent directly to AI for structured data extraction.
              </p>
            </div>
          )}
          {uploadedFile && (uploadedFile.type === 'text/xml' || uploadedFile.type === 'application/xml' || uploadedFile.name?.toLowerCase().endsWith('.xml')) && (
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
              <div className="flex items-center text-sm text-purple-700 dark:text-purple-300">
                <Sparkles className="mr-2 text-purple-500" size={16} />
                <span className="font-medium">XML Text Mode Active</span>
              </div>
              <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                XML content will be sent directly to AI for structured data extraction.
              </p>
            </div>
          )}

          {/* No File Warning */}
          {!uploadedFile && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <div className="flex items-center text-sm text-yellow-700 dark:text-yellow-300">
                <AlertCircle className="mr-2 text-yellow-500" size={16} />
                <span className="font-medium">No file uploaded</span>
              </div>
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                Please upload a PDF, CSV, or XML file to use AI extraction.
              </p>
            </div>
          )}

          {/* Extract Button */}
          <div className="pt-4">
            <button
              onClick={handleExtract}
              disabled={!uploadedFile || getSelectedDataTypes().length === 0}
              className={`w-full flex items-center justify-center px-6 py-3 rounded-lg
                       focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
                       transition-colors font-medium ${
                         !uploadedFile || getSelectedDataTypes().length === 0
                           ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                           : 'bg-purple-600 text-white hover:bg-purple-700'
                       }`}
            >
              <Database className="mr-2" size={20} />
              Extract Data with AI
            </button>
          </div>
        </div>
      )}

      {/* Processing State */}
      {extractionStatus === 'processing' && (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="animate-spin text-purple-500 mb-4" size={48} />
          <p className="text-lg font-medium text-gray-900 dark:text-white">
            Extracting data...
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            AI is analyzing your document and extracting {getSelectedDataTypes().join(', ')} data
          </p>
        </div>
      )}

      {/* Error State */}
      {extractionStatus === 'failed' && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <AlertCircle className="text-red-500 mr-3 mt-0.5" size={20} />
            <div>
              <h4 className="font-medium text-red-800 dark:text-red-200">Extraction Failed</h4>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
              <button
                onClick={() => setExtractionStatus('idle')}
                className="mt-3 flex items-center text-sm text-red-600 dark:text-red-400 hover:underline"
              >
                <RefreshCw size={14} className="mr-1" />
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results Section */}
      {extractionStatus === 'completed' && aiResult && (
        <div className="space-y-6">
          {/* Summary Header */}
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CheckCircle className="text-green-500 mr-3" size={24} />
                <div>
                  <h4 className="font-medium text-green-800 dark:text-green-200">
                    Data Extracted Successfully
                  </h4>
                  {aiResult.businessDate && (
                    <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                      Business Date: <span className="font-semibold">{aiResult.businessDate}</span>
                    </p>
                  )}
                </div>
              </div>
              <ConfidenceBadge value={aiResult.confidence || 0} />
            </div>
          </div>

          {/* Metrics */}
          {metrics && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div>
                  <div className="cp-help">Processing Time</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">
                    {(metrics.processingTimeMs / 1000).toFixed(1)}s
                  </div>
                </div>
                <div>
                  <div className="cp-help">Est. Cost</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">
                    ${metrics.estimatedCost?.toFixed(3) || '0.00'}
                  </div>
                </div>
                {aiResult.documentInfo?.documentType && (
                  <div>
                    <div className="cp-help">Document Type</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
                      {aiResult.documentInfo.documentType}
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={() => setExtractionStatus('idle')}
                className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <RefreshCw size={14} className="mr-1" />
                Re-extract
              </button>
            </div>
          )}

          {/* Extracted Data Sections */}
          <div className="space-y-6">
            {/* Revenue Data */}
            {aiResult.extractedData?.revenue && (
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                  <DollarSign className="mr-2 text-green-500" size={18} />
                  Revenue Data ({aiResult.extractedData.revenue.length} categories)
                </h4>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <RevenueDataTable
                    data={aiResult.extractedData.revenue}
                    onTypeNameChange={handleTypeNameChange}
                    editedNames={revenueTypeOverrides}
                  />
                </div>
              </div>
            )}

            {/* Room Stats */}
            {aiResult.extractedData?.roomStats && (
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                  <Home className="mr-2 text-blue-500" size={18} />
                  Room Statistics
                </h4>
                <RoomStatsCard data={aiResult.extractedData.roomStats} />
              </div>
            )}

            {/* Occupancy Forecast */}
            {aiResult.extractedData?.occupancy && (
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                  <Calendar className="mr-2 text-purple-500" size={18} />
                  Occupancy Data
                </h4>
                <OccupancyCard data={aiResult.extractedData.occupancy} />
              </div>
            )}

            {/* Journal Data */}
            {aiResult.extractedData?.journal && aiResult.extractedData.journal.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                  <BookOpen className="mr-2 text-orange-500" size={18} />
                  Journal Data ({aiResult.extractedData.journal.length} entries)
                </h4>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left px-4 py-2 font-semibold text-gray-600 dark:text-gray-400">Type Name</th>
                        <th className="text-left px-4 py-2 font-semibold text-gray-600 dark:text-gray-400">Category</th>
                        <th className="text-right px-4 py-2 font-semibold text-gray-600 dark:text-gray-400">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {aiResult.extractedData.journal.map((row, i) => (
                        <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-4 py-2 text-gray-800 dark:text-gray-200">{row.typeName}</td>
                          <td className="px-4 py-2">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                              row.category === 'revenue' ? 'bg-green-100 text-green-700' :
                              row.category === 'tax' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {row.category}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-gray-800 dark:text-gray-200">
                            {row.amount != null ? `$${Number(row.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Document Notes */}
          {aiResult.documentInfo?.notes && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <div className="flex items-start">
                <AlertCircle className="text-yellow-500 mr-2 mt-0.5" size={16} />
                <div>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 font-medium">Notes</p>
                  <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
                    {aiResult.documentInfo.notes}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleSaveData}
              className="flex-1 flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg
                       hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 font-medium"
            >
              <CheckCircle className="mr-2" size={20} />
              Confirm & Save Data
            </button>
          </div>
        </div>
      )}

      {/* Navigation — hidden when the wizard frame owns it. */}
      <div className={`justify-between pt-6 mt-6 border-t border-gray-200 dark:border-gray-700 ${hideFooter ? 'hidden' : 'flex'}`}>
        <button
          onClick={onBack}
          className="flex items-center px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <ChevronLeft className="mr-1" size={20} />
          Back
        </button>
        <button
          onClick={onSkip}
          disabled={isSkipping || extractionStatus === 'processing'}
          className="flex items-center px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
        >
          {isSkipping ? 'Reading file…' : skipLabel}
          <ChevronRight className="ml-1" size={20} />
        </button>
      </div>
    </div>
  );
};

export default AiExtractionTab;
